import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { agreementsApi, type AgreementsListParams } from '../api/agreements';
import { ApiError } from '../api/client';
import type { Agreement, AgreementCreateInput, AgreementUpdateInput, Page, UUID, WriteResult } from '../api/types';

export const agreementsKeys = {
  all:    ['agreements'] as const,
  list:   (p: AgreementsListParams) => ['agreements', 'list', p] as const,
  // includeDeleted is part of the key because the response differs (a
  // soft-deleted record returns 404 when includeDeleted=false but the
  // record itself when includeDeleted=true). Caching them under separate
  // keys avoids stale cross-contamination after delete/restore.
  detail: (id: UUID, includeDeleted = false) =>
    ['agreements', 'detail', id, includeDeleted ? 'with-deleted' : 'alive'] as const,
};

// Phase E1: any agreement write may flip its completeness state, so the
// /compliance/incomplete and /compliance/incomplete-fields/* queries must
// re-fetch alongside the agreements cache.
function _invalidateCompliance(qc: ReturnType<typeof useQueryClient>): void {
  qc.invalidateQueries({ queryKey: ['compliance'] });
}

export function useAgreements(
  params: AgreementsListParams,
  opts?: { enabled?: boolean },
) {
  return useQuery<Page<Agreement>, ApiError>({
    queryKey: agreementsKeys.list(params),
    queryFn: () => agreementsApi.list(params),
    // #39 (2026-06-11): zero pills selected → the page skips the fetch
    // and renders the empty-state hint instead.
    enabled: opts?.enabled ?? true,
    // #46 (2026-06-12): filter changes produce new query keys; previous
    // rows stay visible while refetching instead of flashing to Loading.
    placeholderData: keepPreviousData,
  });
}

export function useAgreement(id: UUID | undefined, includeDeleted = false) {
  return useQuery<Agreement, ApiError>({
    queryKey: agreementsKeys.detail(id ?? '', includeDeleted),
    queryFn: () => agreementsApi.get(id!, includeDeleted),
    enabled: !!id,
  });
}

export function useCreateAgreement() {
  const qc = useQueryClient();
  return useMutation<WriteResult<Agreement>, ApiError, AgreementCreateInput>({
    mutationFn: agreementsApi.create,
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: agreementsKeys.all });
      _invalidateCompliance(qc);
      // Cache the entity only when applied; pending writes leave the live
      // cache alone so other viewers don't see optimistic data.
      if (!r.pending) qc.setQueryData(agreementsKeys.detail(r.entity.id), r.entity);
    },
  });
}

export function useUpdateAgreement(id: UUID) {
  const qc = useQueryClient();
  return useMutation<WriteResult<Agreement>, ApiError, AgreementUpdateInput>({
    mutationFn: (p) => agreementsApi.update(id, p),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: agreementsKeys.all });
      _invalidateCompliance(qc);
      if (!r.pending) qc.setQueryData(agreementsKeys.detail(id), r.entity);
    },
  });
}

export function useDeleteAgreement() {
  const qc = useQueryClient();
  return useMutation<WriteResult<Agreement>, ApiError, UUID>({
    mutationFn: agreementsApi.delete,
    onSuccess: () => qc.invalidateQueries({ queryKey: agreementsKeys.all }),
  });
}

export function useRestoreAgreement() {
  const qc = useQueryClient();
  return useMutation<WriteResult<Agreement>, ApiError, UUID>({
    mutationFn: agreementsApi.restore,
    onSuccess: () => qc.invalidateQueries({ queryKey: agreementsKeys.all }),
  });
}


// Beta #11 cascade extension (2026-05-26).
import type { ReferencesReport } from '../api/persons';

export function useAgreementReferences(id: UUID | undefined, enabled = true) {
  return useQuery<ReferencesReport, ApiError>({
    queryKey: ['agreements', 'references', id ?? ''],
    queryFn: () => agreementsApi.references(id!),
    enabled: enabled && !!id,
  });
}

interface CascadeDeleteAgreementVars {
  id: UUID;
  refIds: Record<string, UUID[]>;
}

export function useCascadeDeleteAgreement() {
  const qc = useQueryClient();
  return useMutation<WriteResult<Agreement>, ApiError, CascadeDeleteAgreementVars>({
    mutationFn: ({ id, refIds }) => agreementsApi.cascadeDelete(id, refIds),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: agreementsKeys.all });
      qc.invalidateQueries({ queryKey: ['agreement-parties'] });
      qc.invalidateQueries({ queryKey: ['agreement-documents'] });
      qc.invalidateQueries({ queryKey: ['accounts'] });
      qc.invalidateQueries({ queryKey: ['compliance'] });
    },
  });
}

// ── #262 cascade-Inactivo / cascade-Closed hooks ────────────────────────

import type {
  CascadeFootprint,
  CascadePlan,
  CascadeStatusResponse,
  LifecycleStatus,
} from '../api/agreements';

/** Fetch the cascade footprint for an Agreement transitioning to toStatus.
 *  Disabled when toStatus is null (the modal closes / no transition selected). */
export function useAgreementCascadeFootprint(
  id: UUID | undefined,
  toStatus: LifecycleStatus | null,
) {
  return useQuery<CascadeFootprint, ApiError>({
    queryKey: ['agreements', 'cascade-footprint', id, toStatus],
    queryFn: () => agreementsApi.cascadeFootprint(id!, toStatus!),
    enabled: Boolean(id) && toStatus !== null,
    // Footprint can change between modal open and confirm if other users
    // touch the tree. Refetch on focus to keep the preview honest.
    refetchOnWindowFocus: true,
    staleTime: 0,
  });
}

export interface CascadeStatusApplyVars {
  id: UUID;
  to_status: LifecycleStatus;
  cascade_plan: CascadePlan;
}

export function useCascadeStatusApply() {
  const qc = useQueryClient();
  return useMutation<CascadeStatusResponse, ApiError, CascadeStatusApplyVars>({
    mutationFn: (vars) =>
      agreementsApi.cascadeStatusApply(vars.id, {
        to_status: vars.to_status,
        cascade_plan: vars.cascade_plan,
      }),
    onSuccess: () => {
      // Cascade touches Agreement + descendants. Invalidate broadly.
      qc.invalidateQueries({ queryKey: agreementsKeys.all });
      qc.invalidateQueries({ queryKey: ['accounts'] });
      qc.invalidateQueries({ queryKey: ['account-insurance'] });
      qc.invalidateQueries({ queryKey: ['insurance-policies'] });
      qc.invalidateQueries({ queryKey: ['change-requests'] });
      qc.invalidateQueries({ queryKey: ['compliance'] });
      _invalidateCompliance(qc);
    },
  });
}

// ── Phase 3 (C-166): faithful reactivate ────────────────────────────────────

export function useAgreementReactivateFootprint(
  id: UUID | undefined,
  enabled: boolean,
) {
  return useQuery<CascadeFootprint, ApiError>({
    queryKey: ['agreements', 'reactivate-footprint', id],
    queryFn: () => agreementsApi.reactivateFootprint(id!),
    // The footprint = the cascade-touched children this reactivation will
    // restore; to_status is the reason-driven reopen target (server-decided).
    enabled: Boolean(id) && enabled,
    refetchOnWindowFocus: true,
    staleTime: 0,
  });
}

export interface ReactivateApplyVars {
  id: UUID;
  reactivate_plan: CascadePlan;
}

export function useReactivateApply() {
  const qc = useQueryClient();
  return useMutation<CascadeStatusResponse, ApiError, ReactivateApplyVars>({
    mutationFn: (vars) =>
      agreementsApi.reactivate(vars.id, { reactivate_plan: vars.reactivate_plan }),
    onSuccess: () => {
      // Reactivate touches Agreement + restored descendants. Invalidate broadly.
      qc.invalidateQueries({ queryKey: agreementsKeys.all });
      qc.invalidateQueries({ queryKey: ['accounts'] });
      qc.invalidateQueries({ queryKey: ['account-insurance'] });
      qc.invalidateQueries({ queryKey: ['insurance-policies'] });
      qc.invalidateQueries({ queryKey: ['change-requests'] });
      qc.invalidateQueries({ queryKey: ['compliance'] });
      _invalidateCompliance(qc);
    },
  });
}
